// SPDX-License-Identifier: Elastic-2.0
/**
 * Toggle — accessible pill switch.
 *
 * Renders as a button with `role="switch"` + `aria-checked` (the
 * ARIA-correct pattern for an on/off control with a label rendered
 * outside the button). The whole row — label + description + switch —
 * is wrapped in a `<label>` so clicking anywhere toggles, matching the
 * JSX prototype's behaviour.
 *
 * Keyboard: Space and Enter both toggle (native button behaviour);
 * `role="switch"` is announced by VoiceOver / NVDA / JAWS.
 *
 * Animation: thumb slides 240ms with `--ease-soft`. The global reduced-
 * motion media query (theme/index.css) collapses transitions to ~0ms,
 * but we also explicitly disable the thumb transition here so the
 * intent is local and obvious.
 */
import { useId } from "react";

import styles from "./Toggle.module.css";

export interface ToggleProps {
  /** Current on/off state. Controlled component — no internal state. */
  checked: boolean;
  /** Called with the new value when the user toggles. */
  onChange: (value: boolean) => void;
  /** Visible label, also used by screen readers. */
  label: string;
  /** Optional supporting copy under the label. */
  description?: string;
  /** Disable interaction + dim visually. */
  disabled?: boolean;
  /** Override the `aria-label` (defaults to `label`). */
  "aria-label"?: string;
}

export function Toggle({
  checked,
  onChange,
  label,
  description,
  disabled,
  "aria-label": ariaLabelOverride,
}: ToggleProps): JSX.Element {
  const labelId = useId();
  const descriptionId = useId();

  function handleClick(): void {
    if (disabled) return;
    onChange(!checked);
  }

  return (
    <label
      className={styles.row}
      data-disabled={disabled ? "true" : undefined}
      onClick={(event) => {
        // Prevent the implicit label->control click from firing twice
        // (once from the click on the row, once from the synthetic click
        // the label forwards to the button).
        if (event.target instanceof HTMLElement && event.target.tagName === "BUTTON") {
          return;
        }
        event.preventDefault();
        handleClick();
      }}
    >
      <span className={styles.textCol}>
        <span id={labelId} className={styles.label}>
          {label}
        </span>
        {description ? (
          <span id={descriptionId} className={styles.description}>
            {description}
          </span>
        ) : null}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-labelledby={ariaLabelOverride ? undefined : labelId}
        aria-label={ariaLabelOverride}
        aria-describedby={description ? descriptionId : undefined}
        aria-disabled={disabled || undefined}
        disabled={disabled}
        className={styles.switch}
        onClick={(event) => {
          event.stopPropagation();
          handleClick();
        }}
      >
        <span aria-hidden="true" className={styles.thumb} />
      </button>
    </label>
  );
}
